import { ReactNode } from 'react'

import { RemoveCircleOutline } from '@mui/icons-material'
import { Box, IconButton, Stack, Tooltip } from '@mui/material'

import { TextField } from '@workwave-tidal/core/components'
import FreeSelect from './components/FreeSelect'

import type { AddLoadOption as Option } from '../../types'
import { BulkLoadExact } from '../../../../../types'
import { useTexts } from './useTexts'

interface Props {
  onUpdateSelect: (oldName: string, newName: string) => void
  onUpdateValue: (name: string, newValue: string) => void
  onRemove: (name: string) => void
  value: BulkLoadExact[]
  disabled?: boolean
  options: Option[]
  error: boolean
  errorText?: ReactNode
  ordersCount: number
}

type LoadError = { name?: string; value?: string }

const parseLoadsErrorString = (errorString: string): Record<string, LoadError> => {
  if (!errorString) return {}
  try {
    return JSON.parse(errorString) as Record<string, LoadError>
  } catch {
    return {}
  }
}

export function Field(props: Props) {
  const {
    value,
    onRemove,
    disabled,
    onUpdateSelect,
    onUpdateValue,
    options,
    error,
    errorText,
    ordersCount,
  } = props

  const texts = useTexts()

  const selectedLoadNames = value.map(load => load.name)

  const errors = error ? parseLoadsErrorString(errorText as string) : {}

  return (
    <Stack spacing={2}>
      {value.map(load => {
        const loadError = errors[load.name]
        return (
          <Stack key={load.name} direction="row" spacing={1} alignItems="flex-start">
            <Box flex={3}>
              <FreeSelect
                label={texts.loadName}
                initialOptions={options}
                value={load.name}
                onChange={newValue => onUpdateSelect(load.name, newValue)}
                selectedValues={selectedLoadNames}
                error={!!loadError?.name}
                errorText={loadError?.name}
                ordersCount={ordersCount}
              />
            </Box>
            <Box flex={1}>
              <>
                <TextField
                  type="number"
                  fullWidth
                  placeholder={texts.loadValue}
                  disabled={disabled}
                  value={load.value}
                  onChange={e => onUpdateValue(load.name, e.target.value)}
                  label={texts.loadValue}
                  error={!!loadError?.value}
                  errorIcon={null}
                  helperText={loadError?.value}
                />
              </>
            </Box>
            <Box flexShrink={0} display="flex" alignItems="center" height="56px">
              <Tooltip title={texts.deleteLoadTooltip}>
                <IconButton color="error" onClick={() => onRemove(load.name)}>
                  <RemoveCircleOutline />
                </IconButton>
              </Tooltip>
            </Box>
          </Stack>
        )
      })}
    </Stack>
  )
}
